import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { Box } from "@mui/material";
import type { WorkOrderMatch } from "../repositories/sawWriteRepository";
import { IconChip } from "./primitives";

// Running WO is the normal case: no tint. Others: tint + bar.
const STATES = {
	current: {
		label: "Running WO",
		short: "Running",
		color: "#2e7d32",
		Icon: CheckCircleOutlineIcon,
		tint: null,
	},
	next: {
		label: "For next work order",
		short: "Next WO",
		color: "#8a5a00",
		Icon: ScheduleIcon,
		tint: { bg: "#fff8e6", bar: "#e0a100" },
	},
	none: {
		label: "Not planned",
		short: "Not planned",
		color: "#c62828",
		Icon: BlockIcon,
		tint: { bg: "#fdf0ef", bar: "#c62828" },
	},
	noWorkOrder: {
		label: "No work order to compare",
		short: "No WO",
		color: "#616161",
		Icon: RemoveCircleOutlineIcon,
		tint: { bg: "#f5f6f7", bar: "#9e9e9e" },
	},
} as const;

export const matchState = (match: WorkOrderMatch) =>
	STATES[match ?? "noWorkOrder"];

// Bar on the first cell: box-shadow on <tr> is unreliable.
export const matchRowSx = (match: WorkOrderMatch) => {
	const { tint } = matchState(match);
	return tint
		? {
				bgcolor: tint.bg,
				"& > td:first-of-type": { boxShadow: `inset 6px 0 0 ${tint.bar}` },
			}
		: undefined;
};

export const WorkOrderMatchLabel = ({
	match,
	size = 16,
}: {
	match: WorkOrderMatch;
	size?: number;
}) => {
	const { label, color, Icon } = matchState(match);
	return (
		<Box
			component="span"
			sx={{
				display: "inline-flex",
				alignItems: "center",
				verticalAlign: "middle",
				gap: 0.75,
				color,
				fontSize: size,
				fontWeight: 700,
			}}
		>
			<Icon sx={{ fontSize: size + 4 }} />
			{label}
		</Box>
	);
};

// Short outline chip for narrow columns; full label on hover.
export const WorkOrderMatchChip = ({ match }: { match: WorkOrderMatch }) => {
	const { label, short, color, Icon } = matchState(match);
	return <IconChip label={short} color={color} Icon={Icon} title={label} />;
};
